import * as Yup from 'yup';

import { Box, ButtonV2, DialogV2, Flex, InputV2, TextV2 } from '@withjoy/joykit';
import React, { useEffect } from 'react';
import { validateHour, validateMinute } from './TimePicker.utils';
import { useFormik } from 'formik';

const getTwoDigitsValue = (n: number) => {
  return n < 10 ? `0${n}` : n.toString();
};
export interface TimePickerInterface {
  minutes: number;
  hours: number;
  ampm: 'am' | 'pm';
}

interface TimePickerInputProps {
  hours: string;
  minutes: string;
  ampm: string;
}

const TimePickerDialog: React.FC<{
  title?: string;
  isOpen: boolean;
  onSave: (timeString: string) => void;
  onClose: () => void;
  defaultHours?: string;
  defaultMinutes?: string;
  defaultAmPm?: string;
}> = ({ title = 'Select Time', isOpen, onSave, onClose, defaultHours, defaultMinutes, defaultAmPm }) => {
  const formik = useFormik<TimePickerInputProps>({
    initialValues: {
      hours: defaultHours || '',
      minutes: defaultMinutes ? defaultMinutes.padStart(2, '0') : '',
      ampm: defaultAmPm || 'PM',
    },
    validationSchema: Yup.object<TimePickerInputProps>({
      hours: Yup.string().required(),
      minutes: Yup.string().required(),
      ampm: Yup.string(),
    }),
    validateOnMount: false,
    onSubmit: ({ hours, minutes, ampm }) => {
      const getHourToSave = () => {
        if (ampm === 'AM') {
          if (+hours === 12) return `00`;
          return getTwoDigitsValue(+hours);
        } else {
          if (+hours === 12) return `12`;
          return +hours + 12;
        }
      };

      onSave(`${getHourToSave()}:${getTwoDigitsValue(+minutes)}`);
    },
  });

  useEffect(() => {
    if (isOpen) {
      formik.setFieldValue('hours', defaultHours || '');
      formik.setFieldValue('minutes', defaultMinutes ? defaultMinutes.padStart(2, '0') : '');
      formik.setFieldValue('ampm', defaultAmPm || 'PM');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleHourChange = (value: string) => {
    const isValidHour = validateHour(value);
    let valueToSave = isValidHour ? value : '';

    if (isNaN(+value)) {
      valueToSave = '';
    } else if (+value <= 0) {
      valueToSave = '';
    } else if (+value >= 12) {
      valueToSave = '12';
    }

    formik.setFieldValue('hours', valueToSave);
  };

  const handleMinuteChange = (value: string) => {
    const isValidMinute = validateMinute(value);
    let valueToSave = isValidMinute ? value : '';

    if (isNaN(+value)) {
      valueToSave = '';
    } else if (+value < 0) {
      valueToSave = '';
    } else if (+value >= 59) {
      valueToSave = '59';
    }

    formik.setFieldValue('minutes', valueToSave);
  };

  return (
    <DialogV2
      isOpen={isOpen}
      onClose={onClose}
      overrides={{
        Content: {
          props: {
            minWidth: { _: '100%', sm2: 240 },
            maxWidth: '80%',
            width: 'unset',
            maxHeight: '80%',
            minHeight: 'unset',
            borderRadius: 12,
          },
        },
      }}
    >
      <Box
        backgroundColor="white"
        padding={8}
        borderRadius={12}
        fontFamily="'Inter UI'"
        as={'form'}
        onSubmit={formik.handleSubmit}
      >
        <TextV2
          marginBottom={6}
          fontSize="md"
          fontWeight="semibold"
          textTransform="uppercase"
          color="gray10"
        >
          {title}
        </TextV2>
        <Flex
          justifyContent="center"
          marginBottom={48}
        >
          <Flex flexDirection="column">
            <InputV2
              width={92}
              height={88}
              {...formik.getFieldProps('hours')}
              overrides={{
                Input: {
                  props: {
                    fontSize: '48px',
                    lineHeight: '88px',
                    textAlign: 'center',
                    cursor: 'pointer',
                  },
                },
              }}
              type="text"
              inputMode="numeric"
              maxLength={2}
              required
              max="12"
              min="1"
              onChange={e => handleHourChange(e.target.value)}
            />
          </Flex>
          <TextV2
            fontSize={48}
            paddingX={12}
            height={88}
          >
            :
          </TextV2>
          <Flex flexDirection="column">
            <InputV2
              type="text"
              {...formik.getFieldProps('minutes')}
              overrides={{
                Input: {
                  props: {
                    fontSize: '48px',
                    lineHeight: '88px',
                    textAlign: 'center',
                    cursor: 'pointer',
                  },
                },
              }}
              width={92}
              height={88}
              inputMode="numeric"
              maxLength={2}
              required
              max="59"
              min="0"
              onChange={e => handleMinuteChange(e.target.value)}
            />
          </Flex>
          <Flex
            flexDirection="column"
            marginLeft={16}
            alignItems="center"
            justifyContent="center"
            boxSizing={'border-box'}
            color={'#767676'}
            backgroundColor={'#F7F7F7'}
            borderRadius={2}
            padding={2}
            height={88}
            width={64}
          >
            <Flex
              as="div"
              cursor="pointer"
              onClick={() => formik.setFieldValue('ampm', 'AM')}
              color={formik.values.ampm === 'AM' ? 'black' : 'gray10'}
              backgroundColor={formik.values.ampm === 'AM' ? 'white' : 'unset'}
              outline={formik.values.ampm === 'AM' ? '1px solid #EBEBEB' : 'none'}
              borderRadius={1}
              _hover={{ color: 'black' }}
              width={'100%'}
              height={'100%'}
              justifyContent={'center'}
              alignItems={'center'}
              transition={'all'}
            >
              AM
            </Flex>

            <Flex
              as="div"
              cursor="pointer"
              onClick={() => formik.setFieldValue('ampm', 'PM')}
              color={formik.values.ampm === 'PM' ? 'black' : 'gray10'}
              backgroundColor={formik.values.ampm === 'PM' ? 'white' : 'unset'}
              outline={formik.values.ampm === 'PM' ? '1px solid #EBEBEB' : 'none'}
              borderRadius={1}
              _hover={{ color: 'black' }}
              width={'100%'}
              height={'100%'}
              justifyContent={'center'}
              alignItems={'center'}
              transition={'all'}
            >
              PM
            </Flex>
          </Flex>
        </Flex>
        <Flex justifyContent="end">
          <ButtonV2
            minWidth={93}
            height={40}
            variant={'ghost'}
            marginRight={3}
            onClick={() => onClose()}
          >
            CANCEL
          </ButtonV2>
          <ButtonV2
            minWidth={93}
            height={40}
            onClick={formik.submitForm}
            disabled={!formik.isValid}
          >
            OK
          </ButtonV2>
        </Flex>
      </Box>
    </DialogV2>
  );
};

export default TimePickerDialog;
